/**
 * useShellDomainAttack - the attack side: the modifier ledger, opposed and
 * instant actions, stances and overwatch, the action drum, what a target's
 * exposures and an attack's aftermath do, the combat engine's hook and the
 * big trigger.
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import useKillTeamDrum from './useKillTeamDrum';
import useRevealProposal from './useRevealProposal';
import { NOTCH_AT_REST } from '../engine/actions/difficultyNotch';
import { countsDownOnAttack, layoutOf } from '../engine/phase/layout';
import { ladderKnows } from './useSceneUnaware';
import useChargeLane from './useChargeLane';
import useChargeRun from './useChargeRun';
import { chargeFireMode } from '../engine/battlefield/charge';
import { actionById } from '../engine/actions/catalog';
import { notOffBow } from '../engine/actions/ram';
import { DEFAULT_REACH_M, reachReason } from '../engine/battlefield/reach';
import { resolveBodyShape } from '../engine/bodyShape';
import { isUnaware } from '../engine/unaware';
import useShellOpposedAction from './useShellOpposedAction';
import useShellLedger from './useShellLedger';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import useShellRam from './useShellRam';
import useShellAreaRuling from './useShellAreaRuling';
import useShellPostures from './useShellPostures';
import useSoloToggle from './useSoloToggle';
import useShellOverwatch from './useShellOverwatch';
import useShellMoveRules from './useShellMoveRules';
import { isEngaged } from '../engine/roster/melee';
import useShellMovementPlates from './useShellMovementPlates';
import useShellExposureHazards from './useShellExposureHazards';
import useShellAttackAftermath from './useShellAttackAftermath';
import useShellActionDrum from './useShellActionDrum';
import * as settings from '../engine/settings/resolve';
import useShellTrigger from './useShellTrigger';
import useCombat from './useCombat';
import * as multiplayer from '../multiplayer';

/**
 * The modifier ledger and opposed drum actions. Length: the hooks' argument
 * records (declarative).
 */
function useShellLedgerAndOpposed(s) {
  const {
    hatred,
    setHatred,
    frenzied,
    actor,
    updateCombatantState,
    setAttackCustomMods,
    slot,
    isMelee,
    effectiveSlot,
    attackerCharStats,
    attackToggles,
    setAttackToggles,
    calledShotExposure,
    calledShotReduction,
    ignoreCalledShot,
    targetNoAim,
    hasBerserkCharge,
    effectiveCustomMods,
    unnaturalDosMode,
    applyConditionModifiers,
    condModOptions,
    roundCountdown,
    stackPolicy,
    attackModFinal,
    actorProjection,
    rangeBand,
    currentTab,
    pushHistory,
    capturePreActionSnapshot,
    historyTrack,
    updateTab,
    addNarrative,
    addRollNarrative,
  } = s;
  // ---- The modifier ledger (v8.222.0): the pre-roll truth ----
  const ledgerData = useShellLedger({
    slot,
    effectiveSlot,
    attackToggles,
    setAttackToggles,
    setHatred,
    calledShotReduction,
    hasBerserkCharge,
    isMelee,
    hatred,
    frenzied,
    effectiveCustomMods,
    applyConditionModifiers,
    condModOptions,
    actorProjection,
    currentTab,
    rangeBand,
    attackModFinal,
    attackerCharStats,
    targetNoAim,
    calledShotExposure,
    ignoreCalledShot,
    setAttackCustomMods,
  });

  // An opposed drum action against the current defender, behind one
  // snapshot - see `hooks/useShellOpposedAction`.
  // The drum's difficulty notch: how hard the next opposed action is. Local to this screen, like the chamber.
  const [notch, setNotch] = useState(NOTCH_AT_REST);
  const executeOpposedAction = useShellOpposedAction({
    notch,
    setNotch,
    actor,
    currentTab,
    actorProjection,
    attackerCharStats,
    slot,
    roundCountdown,
    stackPolicy,
    unnaturalDosMode,
    capturePreActionSnapshot,
    pushHistory,
    updateCombatantState,
    updateTab,
    addNarrative,
    addRollNarrative,
    historyTrack,
  });
  return { ledgerData, executeOpposedAction, notch, setNotch };
}

/**
 * Stances, overwatch and the instant-action lookup. Length: the hooks'
 * argument records (declarative).
 */
function useShellPostureDomain(s) {
  const {
    fireMode,
    roster,
    actorId,
    actor,
    updateCombatantState,
    activeTab,
    effectiveSlot,
    actorConds,
    ruleTokens,
    sync,
    ignoreTerrain,
    mapSolids,
    measureLineRef,
    pushHistory,
    capturePreActionSnapshot,
    addRollNarrative,
  } = s;

  // Defensive Stance and Stand - the postures that are not attacks.
  const { defensiveStanceActive, handleToggleDefensiveStance, handleStand } = useShellPostures({
    actor,
    actorConds,
    updateCombatantState,
    addRollNarrative,
    capturePreActionSnapshot,
    pushHistory,
  });

  // Kill zones: declaring one, and who walks into whose arc (v8.308.0).
  const { handleOverwatch, handleTokenSettled } = useShellOverwatch({
    actor,
    actorId,
    tokens: ruleTokens,
    activeTab,
    fireMode,
    measureLineRef,
    roster,
    ignoreTerrain,
    mapSolids,
    turnTrackerState: sync.turnTrackerState,
    effectiveSlot,
    updateCombatantState,
    addRollNarrative,
    capturePreActionSnapshot,
    pushHistory,
  });

  /**
   * Instant actions, by id. A LOOKUP rather than a branch: adding one
   * is a catalog row and an entry here, and the dispatch never learns
   * another id.
   */
  // Go loud is put to the GM by the quiet-work hook, which is declared later: late-bound through its ref.
  const { quietMoveRef } = s;
  const handleGoLoud = useCallback(() => quietMoveRef.current?.(actionById('goLoud')), [quietMoveRef]);
  // Ram asks the GM for the damage, which the resolution (declared later) owns: late-bound the same way.
  const ramRef = useRef(null);
  const handleRam = useCallback(() => ramRef.current?.(), []);
  // The movement plates (2026-10-26): Go Prone, Tactical Advance, Disengage, Jump and Leap.
  const movementPlates = useShellMovementPlates({
    ...s,
    actor,
    updateCombatantState,
    addRollNarrative,
    capturePreActionSnapshot,
    pushHistory,
  });
  const instantActions = useMemo(
    () => ({ stand: handleStand, overwatch: handleOverwatch, goLoud: handleGoLoud, ram: handleRam, ...movementPlates }),
    [handleStand, handleOverwatch, handleGoLoud, handleRam, movementPlates],
  );
  // What a finished move does to the mover: Ran, a lost aim, a spent Tactical Advance, leaving melee (2026-10-26).
  const handleMoveSettled = useShellMoveRules({ ...s, roster, ruleTokens, updateCombatantState, addRollNarrative });
  // A character's own Solo modes (the sheet's switchable bundles): one state write, and a line in the log.
  const toggleStance = useSoloToggle({
    actor,
    updateCombatantState,
    addRollNarrative,
    capturePreActionSnapshot,
    pushHistory,
    historyTrack: s.historyTrack,
  });
  return {
    defensiveStanceActive,
    handleToggleDefensiveStance,
    handleTokenSettled,
    handleMoveSettled,
    instantActions,
    toggleStance,
    ramRef,
  };
}

/**
 * The action drum, exposure hazards and the attack's aftermath. Length: the
 * hooks' argument records (declarative).
 */
function useShellDrum(s) {
  const {
    fireMode,
    targetLocation,
    roster,
    actor,
    updateCombatantState,
    activeTab,
    isMelee,
    effectiveSlot,
    actorConds,
    rawAttackToggles,
    setRawAttackToggles,
    attackToggles,
    setAttackToggles,
    attackMode,
    setAttackMode,
    permissions,
    isLocked,
    liveMap,
    startCooldown,
    suppressionZone,
    actorProjection,
    currentTab,
    addNarrative,
    addRollNarrative,
    spendTurnRef,
    defensiveStanceActive,
    handleToggleDefensiveStance,
    instantActions,
  } = s;

  // A brother's Squad Mode plates (the rule tokens: the positions seam); a press reaches the verbs at call time.
  const { ruleTokens } = s;
  const killTeamDrum = useKillTeamDrum({
    actor,
    roster: s.sharedRoster ?? roster,
    tokens: ruleTokens,
    killTeams: s.killTeams ?? null,
    limit: settings.resolveOneSquadAbilityPerTurn(permissions),
    isGm: !!s.isGm || !s.sessionId,
    verbs: () => s.killTeamVerbs?.current ?? undefined,
    spendTurnRef,
  });

  // ---- The action drum (v8.218.0; decisive-only since v8.236.0) ----
  const {
    actionDrumOpen,
    setActionDrumOpen,
    toggleActionDrum,
    chamberedActionId,
    setChamberedActionId,
    drumDisabledReasons,
    attackTriggerLabel,
    actionDrumProps,
    fireRef,
  } = useShellActionDrum({
    actor,
    actorConds,
    currentTab,
    isLocked,
    isMelee,
    fireMode,
    targetLocation,
    attackToggles,
    setAttackToggles,
    attackMode,
    setAttackMode,
    defensiveStanceActive,
    handleToggleDefensiveStance,
    toggleStance: s.toggleStance,
    squad: killTeamDrum.squad,
    pressSquad: killTeamDrum.pressSquad,
    instantActions,
    spendTurnRef,
    notch: s.notch,
    setNotch: s.setNotch,
    ...drumFacts(s),
  });

  // What an exposed target does to whoever attacks it - Spined, Grasping,
  // curses, the gates, Sanctioned, Taunt and Dread. See the hook.
  const { onTouched: handleTouched, onDread: handleDread } = useShellExposureHazards({
    actor,
    actorProjection,
    activeTab,
    roster,
    measurement: liveMap.measurement,
    dreadPerAttack: settings.resolveDreadPerAttack(permissions),
    updateCombatantState,
    addNarrative,
    // Squad Mode (D12): through the verbs ref - the kill-team's verbs are declared in a later domain (People).
    onFearFailed: (id, fearRating, creature) => void s.killTeamVerbs?.current?.fear(id, fearRating, creature),
  });

  // What an attack roll and a reaction spend - the capacitor, aim, the
  // charge bonus, Suppressing Fire's pinning. See the hook.
  const { onAttackRolled: handleAttackRolled, onReactionTaken: handleReactionTaken } = useShellAttackAftermath({
    actor,
    actorProjection,
    roster,
    activeTab,
    effectiveSlot,
    rawAttackToggles,
    attackMode,
    startCooldown,
    suppressionZone,
    setRawAttackToggles,
    setAttackMode,
    updateCombatantState,
    addRollNarrative,
    addNarrative,
  });
  return {
    actionDrumOpen,
    setActionDrumOpen,
    toggleActionDrum,
    chamberedActionId,
    setChamberedActionId,
    drumDisabledReasons,
    attackTriggerLabel,
    actionDrumProps,
    fireRef,
    // The Squad Mode run and its press, for the actor's strip too (plan modes-rework: one source of plates).
    squadDrum: killTeamDrum.squad,
    pressSquad: killTeamDrum.pressSquad,
    handleTouched,
    handleDread,
    handleAttackRolled,
    handleReactionTaken,
  };
}

/** What the drum's plates are judged on, off the phase, the ladder and the map: plain values, no hooks. */
function drumFacts(s) {
  const { actor, activeTab, currentTab } = s;
  return {
    // Which plates the phase loads, and whether the target has not noticed (a condition, or the ladder).
    drum: layoutOf(s.direction.shown).drum,
    targetUnaware: !!s.unawareIds?.has(activeTab) || isUnaware(currentTab?.target),
    // The ladder knows (Searching and above), so only the GM's hand makes someone Unaware (2026-10-21).
    ladderKnows: ladderKnows(s.direction.frame),
    // Why a charge cannot be run from here (useChargeLane); none with no map to decide.
    chargeRefusal: s.chargeLane.refusal,
    // Strict melee reach (plan map-display, D): the weapon gates' answer, from the map.
    outOfReach: !!s.meleeReachReason,
    // Body to body, by the live map - which says nothing when the map may not decide (useShellLiveMap).
    notInContact: reachReason(s.liveMap?.reachGap, DEFAULT_REACH_M) !== null,
    // Disengage needs an enemy in reach; with him off the map (positions off) the map cannot say, so it is open.
    notEngaged: isEngaged(actor, s.roster ?? [], s.ruleTokens) === false,
    // Ram is a vehicle's own plate, and runs straight off the bow (engine/actions/ram) - by the same switch.
    actorIsVehicle: resolveBodyShape(s.actorProjection) === 'vehicle',
    offBow:
      !!s.liveMap?.measurement &&
      notOffBow(s.ruleTokens?.[actor?.id], s.ruleTokens?.[activeTab], s.vehicleArcPreset ?? 'quarters'),
    // The other side's difficulty is the director's: the GM, or anyone outside a session.
    canSetTheirs: !s.sessionId || !!s.isGm,
  };
}

/**
 * The combat engine's hook and the trigger that fires the chamber. Length:
 * useCombat's argument record, one line per input (declarative).
 */
function useShellResolution(s) {
  const { user, selectedSlot, fireMode, hatred, frenzied, targetLocation, actor, activeTab, pendingAttack } = s;
  const { setPendingAttack, rerollDamage, sessionId, playerId, localCurrentProfile, setAtkProfiles } = s;
  const { currentSlots, weaponOverrides, attackerCharStats, hasHammerBlow, attackToggles, attackMode } = s;
  const { calledShotExposure, calledShotReduction, ignoreCalledShot, hasBerserkCharge, effectiveCustomMods } = s;
  const { resolveExpiredDefenceRef, sync, syncTargetToFirestore, syncNarrativeToFirestore, isLocked } = s;
  const { createPendingDefence, clearPendingDefenceFirestore, isDefencePending, unnaturalDosMode } = s;
  const { blastScatterOn, reliableNeverJamsOn, applyConditionModifiers, condModOptions, roundCountdown } = s;
  const { protagonistRules, averagedDamage, indirectFire, attackBlockedReason, attackModFinal, tabs } = s;
  const { resolveVictims, resolveSpawned, actorProjection, rangeBand, currentTab, pushHistory } = s;
  const { capturePreActionSnapshot, historyTrack, updateTab, addNarrative, spendTurnRef } = s;
  const { handleOverheatDetected, executeOpposedAction, chamberedActionId, setChamberedActionId } = s;
  const { drumDisabledReasons, fireRef, handleTouched, handleDread, handleAttackRolled, handleReactionTaken } = s;
  const { positionsCount, roster, blastSelfPolicy, areaTicksRef, asks, isGm, effectiveSlot } = s;

  // "Who is caught?", asked of the table while positions do not count - see the hook.
  const areaRuling = useShellAreaRuling({
    positionsCount,
    rulesAlone: isGm || !sessionId,
    roster,
    actor,
    activeTab,
    slot: effectiveSlot,
    attackMode,
    blastSelfPolicy,
    ticksRef: areaTicksRef,
    ask: asks.ask,
  });

  const { attackHeardRef } = s;
  const handleAttackHeard = useCallback((info) => attackHeardRef?.current?.(info), [attackHeardRef]);
  // Is this attack a Charge (the toggle, a melee weapon, a body that charges)? Read once: the fire mode and the run.
  const charging = isCharging(s);
  const {
    handleAttack,
    handleRam,
    handleRerollChoice,
    pendingReroll,
    pendingBonusAttack,
    setPendingBonusAttack,
    handleBonusAttackChoice,
    handleDefenceChoice,
    handleDefenceStep,
    acceptRemainingHits,
    defenceResolving,
    defenceChain,
  } = useCombat({
    selectedSlot,
    // A charge strikes once: no Swift or Lightning rides it (engine/battlefield/charge).
    fireMode: chargeFireMode(fireMode, charging),
    attackMod: attackModFinal,
    hatred,
    frenzied,
    rerollDamage,
    targetLocation,
    rangeBand,
    pendingAttack,
    setPendingAttack,
    tabs,
    activeTab,
    currentSlots,
    currentTab,
    setAtkProfiles,
    localCurrentProfile,
    syncNarrativeToFirestore,
    isLocked,
    syncTargetToFirestore,
    pushHistory,
    capturePreActionSnapshot,
    historyTrack,
    updateTab,
    addNarrative,
    attackerCharStats,
    // The table's overrides with the armed weapon's holding rules folded in (plan armoury); never written back.
    weaponOverrides: s.attackOverrides ?? weaponOverrides,
    unnaturalDosMode,
    applyConditionModifiers,
    condModOptions,
    // Who Stealth's ladder says has not noticed: no reaction for them (hooks/useSceneUnaware).
    unawareIds: s.unawareIds,
    // An attack made in Stealth is heard by the ladder (hooks/useShellQuietMoves, late-bound).
    onAttackResolved: handleAttackHeard,
    roundCountdown,
    protagonistRules,
    averagedDamage,
    // Squad Mode: heavy fire on a brother. Through the verbs ref: they are declared in a later domain (People).
    onCohesionHit: (tabId, weapon) => void s.killTeamVerbs?.current?.damage(tabId, weapon),
    // The acting combatant's own conditions modify their to-hit (D1) -
    // including whatever the ground under them is imposing.
    actorConditionContext: actorProjection,
    // Who struck, carried to the log for the display's beat (veiled at the sync boundary).
    actorId: actor?.id ?? null,
    isCalledShot: attackToggles.calledShot,
    calledShotReduction,
    calledShotExposure,
    calledShotIgnored: ignoreCalledShot,
    // Combat action toggles. Aim is here so the breakdown can NAME it -
    // it is in `attackMod` either way, and an unnamed +10 reads as the
    // app inventing a bonus.
    isAimHalf: attackToggles.aimHalf,
    isAimFull: attackToggles.aimFull,
    isAllOutAttack: attackToggles.allOutAttack,
    isGuardedAttack: attackToggles.guardedAttack,
    isCharge: attackToggles.charge,
    isGangUp: attackToggles.gangUp,
    hasBerserkCharge,
    hasHammerBlow,
    // Multiplayer defence
    sessionId,
    playerId,
    user,
    mp: multiplayer,
    createPendingDefence,
    clearPendingDefence: clearPendingDefenceFirestore,
    isDefencePending,
    // The map's answer to "who did that shape catch". Null for every attack
    // that is not an area weapon - see the callback for what it decides.
    resolveVictims,
    resolveSpawned,
    parkScatter: areaRuling.parkScatter,
    blastScatter: blastScatterOn,
    reliableNeverJams: reliableNeverJamsOn,
    onOverheat: handleOverheatDetected,
    onAttackRolled: handleAttackRolled,
    onTouched: handleTouched,
    onDread: handleDread,
    attackCustomMods: effectiveCustomMods,
    suppressing: attackMode === 'suppressing',
    onReactionTaken: handleReactionTaken,
    attackBlockedReason,
    indirectNoLoS: indirectFire.noLoS,
    // Turn tracker (for countdown dual-mode)
    turnTrackerState: sync.turnTrackerState,
  });

  // The big trigger fires whatever is chambered (v8.236.0); the drum's
  // deferred fire and the expired-reaction sweep bind to the attack here.
  // The attack every path fires - the trigger, the drum, the keys - with the area question in front of it.
  const attackRef = useRef(handleAttack);
  attackRef.current = handleAttack;
  const { fire } = areaRuling;
  // A hidden actor's act on a player is put to the GM first, on a card over the map: reveal, or act veiled.
  const { propose, revealAsk } = useRevealProposal({
    actor,
    activeTab,
    targetName: s.currentTab?.target?.name || s.currentTab?.label,
    hidden: s.hidden,
    reveal: s.hideReveal?.reveal,
  });
  const fireAttack = useChargedFire(s, charging, { actor, activeTab, fire, propose, attackRef });
  const ramRuling = useRamRuling(s, handleRam, propose);
  const opposed = useCallback((action) => propose(() => executeOpposedAction(action)), [propose, executeOpposedAction]);
  const handleAttackOrChambered = useShellTrigger({
    handleAttack: fireAttack,
    handleDefenceChoice,
    fireRef,
    resolveExpiredDefenceRef,
    chamberedActionId,
    setChamberedActionId,
    drumDisabledReasons,
    pendingAttack,
    executeOpposedAction: opposed,
    actor,
    spendTurnRef,
    quietMoveRef: s.quietMoveRef,
    charging,
  });
  return {
    handleRerollChoice,
    pendingReroll,
    pendingBonusAttack,
    setPendingBonusAttack,
    handleBonusAttackChoice,
    handleDefenceChoice,
    handleDefenceStep,
    acceptRemainingHits,
    defenceResolving,
    defenceChain,
    handleAttackOrChambered,
    areaRuling,
    ramRuling,
    revealAsk,
  };
}

/**
 * The trigger's fire, with the Charge in it (useChargeRun): the reveal question first, then the run, then the
 * strike. `charging` is the shell's one reading of `isCharging`, shared with the fire mode.
 */
function useChargedFire(s, charging, { actor, activeTab, fire, propose, attackRef }) {
  const charge = useChargeRun({
    lane: s.chargeLane,
    charging,
    isHidden: (id) => !!s.hidden?.hiddenIds?.has(id),
    actor,
    activeTab,
    tokens: s.ruleTokens,
    writeToken: s.writeToken,
    handleMapMove: s.handleMapMove,
    onTokenSettled: s.handleTokenSettled,
    onMoveSettled: s.handleMoveSettled,
    refuse: (text) => s.addNarrative(activeTab, { type: 'info', text, timestamp: Date.now() }),
    chargeOriginRef: s.chargeOriginRef,
  });
  const fireAttack = useCallback(
    () => propose(() => charge(() => fire(() => attackRef.current()))),
    [fire, propose, charge, attackRef],
  );
  return fireAttack;
}

/** The Charge is up: its toggle, a melee weapon, and a body that charges (a vehicle's charge is its Ram). */
function isCharging(s) {
  return !!s.attackToggles?.charge && !!s.isMelee && resolveBodyShape(s.actorProjection) !== 'vehicle';
}

/**
 * A vehicle's ram (plan map-display, D): the drum's plate asks for the GM's
 * ruling (useShellRam) - through the reveal proposal, since it reaches a
 * player. The bow's share is taken where the hit lands (useApplyHits).
 */
function useRamRuling(s, handleRam, propose) {
  const { actor, activeTab, currentTab, isGm, sessionId, asks, ramRef } = s;
  const ram = useShellRam({
    rulesAlone: isGm || !sessionId,
    ask: asks.ask,
    actorId: actor?.id,
    actorName: actor?.name,
    targetId: activeTab,
    targetName: currentTab?.target?.name || currentTab?.label,
    handleRam,
  });
  const { press } = ram;
  useLayoutEffect(() => {
    ramRef.current = () => propose(press);
  }, [ramRef, propose, press]);
  return ram;
}

/**
 * @param {object} s - the shell scope (everything the table domain and those
 *   before it declared).
 * @returns {object} the ledger, the drum and its trigger label, the stance,
 *   the pending reroll / bonus-attack / defence state and verbs, and
 *   `handleAttackOrChambered`.
 */
export default function useShellDomainAttack(shell) {
  // An ATTACK counts timed conditions down only where the phase says so (Combat). In Narrative and
  // Stealth there are no rounds for an attack to stand for: time passes when the GM says it does.
  const s = { ...shell, roundCountdown: countsDownOnAttack(shell.roundCountdown, shell.direction.frame) };
  const ledgerAndOpposed = useShellLedgerAndOpposed(s);
  const postures = useShellPostureDomain({ ...s, ...ledgerAndOpposed });
  // Can the actor charge the target from where they stand (2026-10-21)? The plate asks; the trigger runs it.
  const { ruleTokens } = s;
  const chargeLane = useChargeLane({ ...s, tokens: ruleTokens });
  const drum = useShellDrum({ ...s, ...ledgerAndOpposed, ...postures, chargeLane });
  const resolution = useShellResolution({ ...s, ...ledgerAndOpposed, ...postures, ...drum, chargeLane });
  return { ...ledgerAndOpposed, ...postures, ...drum, ...resolution };
}
